import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Lock, Mail, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react'

import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import { StatusBadge } from '../../components/ui/Badge'

const Login = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const { data: health, isLoading: isHealthLoading, isError: isHealthError } = useQuery({
    queryKey: ['public-health'],
    queryFn: async () => {
      const response = await fetch('/health')
      if (!response.ok) throw new Error('Health check failed')
      return response.json()
    },
    refetchInterval: 30000,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          username: formData.email.trim(),
          password: formData.password,
        }),
      })

      if (!response.ok) {
        const detail = await response.json().catch(() => null)
        throw new Error(detail?.detail || 'Invalid email or password')
      }

      const tokenData = await response.json()
      localStorage.setItem('auth_token', tokenData.access_token)
      localStorage.setItem('refresh_token', tokenData.refresh_token)

      // Reload the app so the top-level auth guard reads the new token.
      window.location.replace('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password. Please try again.')
      console.error('Login error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  const handleSocialLogin = async (provider: 'google' | 'github') => {
    // TODO: Implement social login
    console.log(`Social login with ${provider}`)
    alert(`Social login with ${provider} - Coming soon!`)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-secondary-900 dark:to-secondary-950 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Welcome Back
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-2">
            Sign in to manage your V2Board sync operations
          </p>
        </div>

        <Card className="relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary-100 dark:bg-primary-900/20 rounded-full -translate-x-16 -translate-y-16" />
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-secondary-100 dark:bg-secondary-800/20 rounded-full translate-x-20 translate-y-20" />

          <div className="relative">
            {error && (
              <div className="mb-6 p-4 rounded-lg border border-error-200 bg-error-50 dark:border-error-800 dark:bg-error-900/20">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
                  <p className="text-error-700 dark:text-error-300">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="admin@casub.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  leftIcon={<Mail className="w-5 h-5" />}
                />

                <div>
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    leftIcon={<Lock className="w-5 h-5" />}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    }
                  />
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-secondary-300 dark:border-secondary-600"
                      />
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">
                        Remember me
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                leftIcon={isLoading ? null : <Lock className="w-5 h-5" />}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-secondary-200 dark:border-secondary-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-secondary-900 text-secondary-500 dark:text-secondary-400">
                    Or continue with
                  </span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleSocialLogin('google')}
                  className="flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.46 1.94 15.8.5 12.48.5 6.047.5 .6 5.947 .6 12.5s5.447 12 11.88 12c4.52 0 8.36-2.56 10.173-6.52.36-1.04.547-2.12.547-3.233 0-.213-.013-.427-.02-.64H12.48z"
                    />
                  </svg>
                  Google
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleSocialLogin('github')}
                  className="flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"
                    />
                  </svg>
                  GitHub
                </Button>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-secondary-600 dark:text-secondary-400">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium transition-colors"
                >
                  Sign up
                </Link>
              </p>
            </div>

            {/* System Status */}
            <div className="mt-8 pt-6 border-t border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-500 dark:text-secondary-400">System Status</span>
                <StatusBadge
                  status={isHealthError ? 'error' : isHealthLoading ? 'pending' : 'active'}
                  label={isHealthError ? 'Offline' : isHealthLoading ? 'Checking' : 'Online'}
                />
              </div>
              <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-1">
                API: {health?.status || (isHealthLoading ? 'checking' : 'unavailable')}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default Login
